import { Outlet, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ShoppingBag, User, MessageCircle, MapPin, Instagram } from "lucide-react";
import { ThemeProvider } from "../context/ThemeContext";
import { useCart } from "../context/CartContext";

const LOGO = "https://customer-assets.emergentagent.com/job_axtreme-salon/artifacts/jfrqlyhn_IMG_0097.png";
const ADDRESS = "737 SW 109th Ave STE 106, Miami, FL 33174";
const MAP_URL = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ADDRESS)}`;
const PHONE_DISPLAY = "+1 (786) 609-6198";
const PHONE_WA = "17866096198";
const EMAIL = "axtremehairstyle@gmail.com";

function Navbar({ onOpenMenu }) {
  const { count } = useCart();
  const loc = useLocation();
  const isHome = loc.pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const solid = !isHome || scrolled;
  return (
    <header
      className="fixed top-0 left-0 right-0 z-40 w-full transition-all duration-300"
      style={{
        background: solid ? "rgba(245,245,245,0.92)" : "transparent",
        backdropFilter: solid ? "blur(10px)" : "none",
        color: solid ? "#1A1A1A" : "#FFFFFF",
        borderBottom: solid ? "1px solid #E0E0E0" : "1px solid transparent",
      }}
      data-testid="site-navbar"
    >
      <div className="w-full px-4 sm:px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <div className="flex items-center gap-3 sm:gap-4">
          <button onClick={onOpenMenu} aria-label="Open menu" data-testid="nav-hamburger" className="p-1 -ml-1" style={{ color: solid ? "#1A1A1A" : "#FFFFFF" }}>
            <Menu className="w-6 h-6" strokeWidth={1.5} />
          </button>
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <img src={LOGO} alt="AXtreme Hair Style" style={{ height: 45 }} className="object-contain" />
          </Link>
        </div>
        <div className="flex items-center gap-5 sm:gap-7" style={{ color: solid ? "#1A1A1A" : "#FFFFFF" }}>
          <Link to="/cart" className="relative hover:text-[#CC2222] transition-colors" data-testid="nav-cart" aria-label="Cart">
            <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
            {count > 0 && (
              <span className="absolute -top-2 -right-2 text-[10px] w-4 h-4 rounded-full flex items-center justify-center" style={{ background: "#CC2222", color: "#fff" }}>{count}</span>
            )}
          </Link>
          <a href={MAP_URL} target="_blank" rel="noreferrer" data-testid="nav-map" aria-label="Map" className="hover:text-[#CC2222] transition-colors">
            <MapPin className="w-5 h-5" strokeWidth={1.5} />
          </a>
          <Link to="/auth" data-testid="nav-account" aria-label="Account" className="hover:text-[#CC2222] transition-colors">
            <User className="w-5 h-5" strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    </header>
  );
}

function HamburgerOverlay({ open, onClose }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);
  const links = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services" },
    { to: "/products", label: "Products" },
    { to: "/booking", label: "Book Appointment" },
    { to: "/our-story", label: "Our Story" },
    { to: "/careers", label: "Careers" },
    { to: "/contact", label: "Contact" },
  ];
  const waMsg = encodeURIComponent("Hi! I'd like to get more information about AXtreme Hair Style.");
  return (
    <>
      <div
        onClick={onClose}
        className={`fixed inset-0 z-50 transition-opacity duration-300 ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        style={{ background: "rgba(0,0,0,0.4)" }}
        aria-hidden="true"
        data-testid="hamburger-backdrop"
      />
      <aside
        className={`fixed left-0 top-0 bottom-0 z-50 w-[280px] max-w-[85vw] flex flex-col transition-transform duration-300 ease-out ${open ? "translate-x-0" : "-translate-x-full"}`}
        style={{ background: "rgba(245,245,245,0.98)", color: "#1A1A1A", backdropFilter: "blur(8px)", borderRight: "1px solid #E0E0E0" }}
        data-testid="hamburger-overlay"
        role="dialog"
        aria-modal="true"
      >
        <div className="px-6 pt-5 pb-3 flex items-center justify-between border-b border-[#E0E0E0]">
          <img src={LOGO} alt="AXtreme" style={{ height: 36 }} />
          <button onClick={onClose} aria-label="Close menu" data-testid="hamburger-close" className="p-1" style={{ color: "#1A1A1A" }}>
            <X className="w-5 h-5" strokeWidth={1.25} />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto px-2 py-6">
          {links.map((l, i) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={onClose}
              className="group block px-6 py-4 border-l-2 border-transparent hover:border-[#CC2222] hover:bg-black/[0.03] transition-all duration-200"
              data-testid={`menu-link-${i}`}
            >
              <span className="text-[16px] uppercase tracking-[0.18em] font-light text-[#1A1A1A] group-hover:text-[#CC2222]">{l.label}</span>
            </Link>
          ))}
          <div className="mt-4 mx-6 border-t border-[#E0E0E0] pt-4 opacity-50">
            <div className="text-[10px] uppercase tracking-[0.3em] mb-3 text-[#2C2825]">Coming Soon</div>
            <div className="px-0 py-2 text-[14px] uppercase tracking-[0.18em] font-light flex items-center gap-3 text-[#1A1A1A]" data-testid="menu-products-soon">
              Products <span className="text-[9px] tracking-[0.25em] px-1.5 py-0.5 border border-[#1A1A1A]/30">Soon</span>
            </div>
            <div className="px-0 py-2 text-[14px] uppercase tracking-[0.18em] font-light flex items-center gap-3 text-[#1A1A1A]" data-testid="menu-academy-soon">
              AXtreme Academy <span className="text-[9px] tracking-[0.25em] px-1.5 py-0.5 border border-[#1A1A1A]/30">Soon</span>
            </div>
          </div>
        </nav>
        <div className="px-6 py-6 border-t border-[#E0E0E0] space-y-4 text-sm">
          <a
            href={`https://wa.me/${PHONE_WA}?text=${waMsg}`}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-3 text-[#1A1A1A] hover:text-[#CC2222] transition-colors"
            data-testid="menu-whatsapp"
          >
            <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
            <span className="tracking-wide">{PHONE_DISPLAY}</span>
          </a>
          <a
            href="https://instagram.com/axtremehairstyle"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-3 text-[#1A1A1A] hover:text-[#CC2222] transition-colors"
            data-testid="menu-instagram"
          >
            <Instagram className="w-4 h-4" strokeWidth={1.5} />
            <span className="tracking-wide">@axtremehairstyle</span>
          </a>
        </div>
      </aside>
    </>
  );
}

function Footer() {
  return (
    <footer className="mt-24" style={{ background: "#EEEEEE", color: "#1A1A1A", borderTop: "1px solid #E0E0E0" }} data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid gap-10 md:grid-cols-4">
        <div>
          <img src={LOGO} alt="AXtreme" style={{ height: 56 }} />
          <p className="mt-4 text-sm opacity-75 max-w-xs">Where Style Meets Perfection.</p>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "#CC2222" }}>Salon</div>
          <ul className="mt-4 space-y-2 text-sm opacity-90">
            <li><Link to="/services" className="hover:text-[#CC2222] transition-colors">Services</Link></li>
            <li><Link to="/services/mens-studio" className="hover:text-[#CC2222] transition-colors">Men's Studio</Link></li>
            <li><Link to="/services/womens" className="hover:text-[#CC2222] transition-colors">Women's Hair</Link></li>
            <li><Link to="/booking" className="hover:text-[#CC2222] transition-colors">Book Appointment</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "#CC2222" }}>Company</div>
          <ul className="mt-4 space-y-2 text-sm opacity-90">
            <li><Link to="/our-story" className="hover:text-[#CC2222] transition-colors">Our Story</Link></li>
            <li><Link to="/careers" className="hover:text-[#CC2222] transition-colors">Careers</Link></li>
            <li><Link to="/contact" className="hover:text-[#CC2222] transition-colors">Contact</Link></li>
            <li><Link to="/products" className="hover:text-[#CC2222] transition-colors">Products</Link></li>
            <li className="opacity-50">AXtreme Academy <span className="text-[10px] uppercase tracking-[0.2em]">— Coming Soon</span></li>
          </ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "#CC2222" }}>Visit</div>
          <p className="mt-4 text-sm opacity-90">{ADDRESS}</p>
          <p className="text-sm opacity-90">{PHONE_DISPLAY}</p>
          <p className="text-sm opacity-90">{EMAIL}</p>
        </div>
      </div>
      <div style={{ borderTop: "1px solid #E0E0E0" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-xs opacity-70">
          © {new Date().getFullYear()} AXtreme Hair Style — Miami, FL
        </div>
      </div>
    </footer>
  );
}

function WhatsAppFloat() {
  const msg = encodeURIComponent("Hi! I'd like to get more information about AXtreme Hair Style.");
  return (
    <a
      href={`https://wa.me/${PHONE_WA}?text=${msg}`}
      target="_blank"
      rel="noreferrer"
      className="fixed bottom-6 right-6 z-30 w-14 h-14 rounded-full flex items-center justify-center text-white wa-pulse"
      style={{ background: "#25D366" }}
      data-testid="whatsapp-float"
      aria-label="WhatsApp"
    >
      <MessageCircle className="w-6 h-6" />
    </a>
  );
}

function LayoutInner() {
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const isHome = loc.pathname === "/";
  const padTop = isHome ? "" : "pt-[72px]";
  return (
    <div className="theme-miami" style={{ minHeight: '100vh' }}>
      <Navbar onOpenMenu={() => setOpen(true)} />
      <HamburgerOverlay open={open} onClose={() => setOpen(false)} />
      <main className={padTop}><Outlet /></main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}

export default function Layout() {
  return (
    <ThemeProvider>
      <LayoutInner />
    </ThemeProvider>
  );
}
